import React, { useState } from 'react';
import { useFormik } from 'formik';
import * as Yup from 'yup';

// @material-ui/core components
import { makeStyles } from "@material-ui/core/styles";
import TextField from "@material-ui/core/TextField";
import Divider from "@material-ui/core/Divider";
import FormControlLabel from "@material-ui/core/FormControlLabel";
import Switch from "@material-ui/core/Switch";
import Typography from "@material-ui/core/Typography";
import CircularProgress from "@material-ui/core/CircularProgress";
import Box from "@material-ui/core/Box";

// core components
import GridContainer from "components/Grid/GridContainer.js";
import GridItem from "components/Grid/GridItem.js";
import Button from "components/CustomButtons/Button.js";

// Specialized components
import HomepageCategoryFileUpload from "./HomepageCategoryFileUpload";

// services
import HomepageCategoriesService from "../../services/HomepageCategoriesService";

const useStyles = makeStyles((theme) => ({
  submitButton: {
    float: "right",
    marginTop: "20px"
  },
  cancelButton: {
    float: "right",
    marginTop: "20px",
    marginRight: "10px"
  },
  divider: {
    marginTop: "30px",
    marginBottom: "20px",
  },
  sectionTitle: {
    fontSize: "0.85rem",
    fontWeight: "bold",
    color: "#2c3e50",
    textTransform: "uppercase",
    marginBottom: '15px'
  },
  loadingContainer: {
    display: 'flex',
    alignItems: 'center',
    gap: '10px',
    marginTop: '20px',
    color: theme.palette.primary.main
  }
}));

const validationSchema = Yup.object().shape({
    displayNameEn: Yup.string().nullable(),
    displayNameEs: Yup.string().nullable(),
    displayNamePt: Yup.string().nullable(),
    displayNameFr: Yup.string().nullable(),
});

export default function HomepageCategoryForm({ initialValues, onSuccess, onCancel }) {
  const classes = useStyles();
  const [isUploading, setIsUploading] = useState(false);

  const formik = useFormik({
    enableReinitialize: true,
    initialValues: {
      id: initialValues.id || null,
      dealerId: initialValues.dealerId || '',
      categoryId: initialValues.categoryId || 0,
      subcategoryId: initialValues.subcategoryId || 0,
      displayNameEn: initialValues.displayNameEn || initialValues.originalNameEn || '',
      displayNameEs: initialValues.displayNameEs || initialValues.originalNameEs || '',
      displayNamePt: initialValues.displayNamePt || initialValues.originalNamePt || '',
      displayNameFr: initialValues.displayNameFr || initialValues.originalNameFr || '',
      originalNameEn: initialValues.originalNameEn || '',
      originalNameEs: initialValues.originalNameEs || '',
      originalNamePt: initialValues.originalNamePt || '',
      originalNameFr: initialValues.originalNameFr || '',
      subcategoryNameEn: initialValues.subcategoryNameEn || '',
      subcategoryNameEs: initialValues.subcategoryNameEs || '',
      subcategoryNamePt: initialValues.subcategoryNamePt || '',
      subcategoryNameFr: initialValues.subcategoryNameFr || '',
      subcategorySlugEn: initialValues.subcategorySlugEn || '',
      subcategorySlugEs: initialValues.subcategorySlugEs || '',
      subcategorySlugPt: initialValues.subcategorySlugPt || '',
      subcategorySlugFr: initialValues.subcategorySlugFr || '',
      imagePath: initialValues.imagePath || '',
      status: initialValues.status === 1,
      sortOrder: initialValues.sortOrder || 0,
    },
    validationSchema: validationSchema,
    onSubmit: async (values) => {
      try {
        const payload = {
          dealer_id: values.dealerId,
          category_id: values.categoryId,
          subcategory_id: values.subcategoryId,
          display_name_en: values.displayNameEn,
          display_name_es: values.displayNameEs,
          display_name_pt: values.displayNamePt,
          display_name_fr: values.displayNameFr,
          original_name_en: values.originalNameEn,
          original_name_es: values.originalNameEs,
          original_name_pt: values.originalNamePt,
          original_name_fr: values.originalNameFr,
          subcategory_name_en: values.subcategoryNameEn,
          subcategory_name_es: values.subcategoryNameEs,
          subcategory_name_pt: values.subcategoryNamePt,
          subcategory_name_fr: values.subcategoryNameFr,
          subcategory_slug_en: values.subcategorySlugEn,
          subcategory_slug_es: values.subcategorySlugEs,
          subcategory_slug_pt: values.subcategorySlugPt,
          subcategory_slug_fr: values.subcategorySlugFr,
          image_path: values.imagePath,
          status: values.status ? 1 : 0,
          sort_order: parseInt(values.sortOrder)
        };

        if (values.id) {
          await HomepageCategoriesService.update(values.id, payload);
          onSuccess(`Settings updated successfully.`);
        } else {
          await HomepageCategoriesService.create(payload);
          onSuccess("Category created successfully.");
        }
      } catch (error) {
        console.error('Operation failed:', error);
        alert(error.response?.data?.message || 'Failed to save changes');
      }
    },
  });

  const handleFileChange = async (file) => {
    if (file === null) {
      formik.setFieldValue('imagePath', null);
      return;
    }

    try {
      setIsUploading(true);
      const response = await HomepageCategoriesService.uploadImage(file, formik.values.dealerId);
      if (response.data && response.data.fullUrl) {
        formik.setFieldValue('imagePath', response.data.fullUrl);
      }
    } catch (error) {
      console.error('Image upload in form failed:', error);
      alert('Failed to upload image. Please try again.');
    } finally {
      setIsUploading(false);
    }
  };

  return (
    <form onSubmit={formik.handleSubmit}>
      <GridContainer>
        {/* Localized Names Section */}
        <GridItem xs={12} md={6}>
          <TextField
            fullWidth
            id="displayNameEn"
            name="displayNameEn"
            label="Display Name (EN)"
            value={formik.values.displayNameEn}
            onChange={formik.handleChange}
            margin="normal"
          />
        </GridItem>
        <GridItem xs={12} md={6}>
          <TextField
            fullWidth
            id="displayNameEs"
            name="displayNameEs"
            label="Display Name (ES)"
            value={formik.values.displayNameEs}
            onChange={formik.handleChange}
            margin="normal"
          />
        </GridItem>
        <GridItem xs={12} md={6}>
          <TextField
            fullWidth
            id="displayNamePt"
            name="displayNamePt"
            label="Display Name (PT)"
            value={formik.values.displayNamePt}
            onChange={formik.handleChange}
            margin="normal"
          />
        </GridItem>
        <GridItem xs={12} md={6}>
          <TextField
            fullWidth
            id="displayNameFr"
            name="displayNameFr"
            label="Display Name (FR)"
            value={formik.values.displayNameFr}
            onChange={formik.handleChange}
            margin="normal"
          />
        </GridItem>

        <GridItem xs={12}>
            <Divider className={classes.divider} />
            <Typography className={classes.sectionTitle}>Visual Assets</Typography>
        </GridItem>

        <GridItem xs={12}>
          <HomepageCategoryFileUpload 
            initialPreview={formik.values.imagePath}
            onFileSelect={handleFileChange}
          />
        </GridItem>

        <GridItem xs={12}>
            <Divider className={classes.divider} />
        </GridItem>

        <GridItem xs={12}>
            <FormControlLabel
                control={
                    <Switch
                        checked={formik.values.status}
                        onChange={formik.handleChange}
                        name="status"
                        color="primary"
                    />
                }
                label="Active Status"
            />
        </GridItem>
      </GridContainer>

      <footer>
        {isUploading && (
            <Box className={classes.loadingContainer}>
                <CircularProgress size={20} />
                <Typography variant="body2">Capturing image...</Typography>
            </Box>
        )}
        <Button 
          color="primary" 
          type="submit" 
          className={classes.submitButton}
          disabled={formik.isSubmitting || isUploading}
        >
          {formik.isSubmitting ? 'Saving...' : 'Save Category'}
        </Button>
        <Button 
          color="white" 
          onClick={onCancel} 
          className={classes.cancelButton}
          disabled={formik.isSubmitting}
        >
          Cancel
        </Button>
      </footer>
    </form>
  );
}
